import { usePage } from '@inertiajs/react'
import { useEffect } from 'react'
import { Toaster, toast } from 'sonner'

import { useTheme } from '@/lib/theme'
import type { FlashProps, PageProps } from '@/types/inertia'

/* -------------------------------------------------------------------------
 * Rails flash → Sonner toast bridge.
 *
 * Inertia ships `page.props.flash` (a hash of severity → message) on every
 * response. We watch that object and fire a toast for each non-empty value.
 * Rails consumes flash exactly once per request cycle, so a given message
 * only arrives on the response after the controller set it — no de-dupe
 * logic needed as long as mutations follow the PRG pattern.
 *
 * Server mapping:
 *   flash[:notice]  → success toast (Devise default)
 *   flash[:alert]   → error toast   (Devise default)
 *   flash[:success] → success toast
 *   flash[:error]   → error toast
 *   flash[:warning] → warning toast
 *   flash[:info]    → info toast
 * ---------------------------------------------------------------------- */

type ToastFn = (message: string) => void

const dispatchers: Record<string, ToastFn> = {
  notice: toast.success,
  success: toast.success,
  alert: toast.error,
  error: toast.error,
  warning: toast.warning,
  info: toast.info,
}

function dispatch(flash: FlashProps) {
  for (const [key, value] of Object.entries(flash)) {
    if (!value) continue
    const fire = dispatchers[key] ?? ((m: string) => toast(m))
    fire(value)
  }
}

export function Flash() {
  const { flash } = usePage<PageProps>().props
  const { theme } = useTheme()

  useEffect(() => {
    dispatch(flash)
  }, [flash])

  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      richColors
      closeButton
      toastOptions={{
        classNames: {
          toast:
            'rounded-md border border-border bg-card text-card-foreground shadow-lg',
        },
      }}
    />
  )
}
